/**
 * Iter 205 PART A — Trainings page (planning + attendance + PDFs).
 * Real event-level training with participants. PLANNED != ATTENDED.
 * Preview-first for Training Record / Attendance / Master List PDFs.
 */
import React, { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { PageHeader, Card, Btn, Modal, Input, Select, Badge, Textarea, Table } from "@/components/UI";
import { openPdfPreview } from "@/lib/pdfPreview";
import { GraduationCap, FileText, Plus, CheckCircle2, Users, Trash2 } from "lucide-react";
import { todayTR } from "@/lib/date";

export default function TrainingsPage() {
  const { user } = useAuth();
  const [rows, setRows] = useState([]);
  const [emps, setEmps] = useState([]);
  const [newOpen, setNewOpen] = useState(false);
  const [detail, setDetail] = useState(null);
  const [form, setForm] = useState({ title: "", training_type: "GMP", training_date: todayTR(),
                                        trainer_type: "internal", trainer_name: "", external_organization: "",
                                        department: "", subject: "", duration_hours: "", participant_ids: [] });

  const load = useCallback(async () => {
    try {
      const r = await api.get("/trainings");
      setRows(Array.isArray(r) ? r : []);
    } catch { setRows([]); }
  }, []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { api.get("/employees?status=active").then(r => setEmps(r.employees || [])).catch(() => setEmps([])); }, []);

  const save = async () => {
    try {
      await api.post("/trainings", { ...form, duration_hours: parseFloat(form.duration_hours) || null });
      setNewOpen(false); setForm({ ...form, title: "", participant_ids: [] }); load();
    } catch (e) { alert(e.response?.data?.detail || "Kaydedilemedi"); }
  };

  const openDetail = async (id) => {
    const r = await api.get(`/trainings/${id}`);
    setDetail(r);
  };

  const updateAttendance = async (tid, employee_id, status) => {
    try {
      await api.patch(`/trainings/${tid}/attendance`, { employee_id, attendance_status: status });
      openDetail(tid); load();
    } catch (e) { alert(e.response?.data?.detail || "Güncellenemedi"); }
  };

  const complete = async (id) => {
    if (!confirm("Eğitim TAMAMLANDI olarak işaretlensin mi? Katılımcı durumları OTOMATİK değişmez (§5-6).")) return;
    await api.post(`/trainings/${id}/complete`);
    openDetail(id); load();
  };

  const del = async (id) => {
    if (!confirm("Eğitim silinsin mi?")) return;
    await api.delete(`/trainings/${id}`);
    setDetail(null); load();
  };

  const dlPdf = async (path, filename) => {
    try {
      const blob = await api.getBlob(path);
      openPdfPreview(blob, filename);
    } catch (e) { alert(e.response?.data?.detail || "PDF alınamadı"); }
  };

  return (
    <div data-testid="trainings-page">
      <div className="flex items-center justify-between">
        <PageHeader title="Eğitimler" description="Gerçek eğitim etkinlikleri, katılımcılar, katılım kaydı ve kontrollü PDF çıktıları"/>
        <div className="flex gap-2 mt-2">
          <Btn variant="ghost" onClick={() => dlPdf("/trainings/master-list/pdf", "Egitim_Ana_Listesi.pdf")} data-testid="trn-master-pdf" title="Eğitim Ana Liste PDF"><FileText size={14}/></Btn>
          <Btn onClick={() => setNewOpen(true)} data-testid="trn-new"><Plus size={14}/> Yeni Eğitim</Btn>
        </div>
      </div>

      <Card className="p-2 mt-3">
        <Table columns={[
          { label: "No", render: r => <span className="font-mono text-xs cursor-pointer text-indigo-600" onClick={() => openDetail(r.id)}>{r.training_no}</span> },
          { label: "Başlık", render: r => r.title },
          { label: "Tarih", render: r => r.training_date },
          { label: "Eğitmen", render: r => r.trainer_name || r.external_organization || "—" },
          { label: "Katılım", render: r => `${r.attended_count || 0}/${r.participant_count || 0}` },
          { label: "Durum", render: r => <Badge level={r.status === "TAMAMLANDI" ? "ok" : "warn"}>{r.status}</Badge> },
          { label: "İşlem", render: r => (
            <div className="flex gap-1">
              <Btn size="sm" variant="ghost" onClick={() => dlPdf(`/trainings/${r.id}/pdf`, `Egitim_${r.training_no}.pdf`)} data-testid={`trn-pdf-${r.id}`}><FileText size={11}/></Btn>
              <Btn size="sm" variant="ghost" onClick={() => dlPdf(`/trainings/${r.id}/attendance/pdf`, `Katilim_${r.training_no}.pdf`)} data-testid={`trn-att-pdf-${r.id}`}><Users size={11}/></Btn>
            </div>
          ) },
        ]} rows={rows}/>
      </Card>

      <Modal open={newOpen} onClose={() => setNewOpen(false)} title="Yeni Eğitim Planla" size="lg">
        <div className="text-xs bg-sky-50 border border-sky-200 rounded p-2 mb-3 text-sky-900">
          Katılımcılar eklendiğinde başlangıç durumu <b>PLANLANDI</b> olur. Katılım bilgisi eğitim gününde ayrıca kaydedilmelidir (§5).
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <Input label="Başlık *" value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} data-testid="trn-title"/>
          <Input label="Tür" value={form.training_type} onChange={e => setForm({ ...form, training_type: e.target.value })}/>
          <Input type="date" label="Tarih *" value={form.training_date} onChange={e => setForm({ ...form, training_date: e.target.value })}/>
          <Input type="number" step="0.5" label="Süre (saat)" value={form.duration_hours} onChange={e => setForm({ ...form, duration_hours: e.target.value })}/>
          <Select label="Eğitmen Türü" value={form.trainer_type} onChange={e => setForm({ ...form, trainer_type: e.target.value })}>
            <option value="internal">İç Eğitmen</option>
            <option value="external">Dış Eğitmen</option>
          </Select>
          <Input label="Eğitmen Adı" value={form.trainer_name} onChange={e => setForm({ ...form, trainer_name: e.target.value })}/>
          {form.trainer_type === "external" && (
            <Input label="Dış Kuruluş" value={form.external_organization} onChange={e => setForm({ ...form, external_organization: e.target.value })}/>
          )}
          <Input label="Departman" value={form.department} onChange={e => setForm({ ...form, department: e.target.value })}/>
        </div>
        <Textarea label="Konu / İçerik" value={form.subject} onChange={e => setForm({ ...form, subject: e.target.value })} rows={2}/>
        <div className="mt-3">
          <div className="text-xs uppercase font-semibold text-slate-600 mb-1">Katılımcılar ({form.participant_ids.length})</div>
          <div className="max-h-40 overflow-y-auto border rounded p-2 bg-slate-50/50 text-xs">
            {emps.map(e => (
              <label key={e.id} className="flex items-center gap-2 py-0.5 cursor-pointer">
                <input type="checkbox" checked={form.participant_ids.includes(e.id)}
                  onChange={ev => {
                    const arr = ev.target.checked
                      ? [...form.participant_ids, e.id]
                      : form.participant_ids.filter(x => x !== e.id);
                    setForm({ ...form, participant_ids: arr });
                  }} data-testid={`trn-emp-${e.id}`}/>
                <span>{e.first_name} {e.last_name} {e.department && <span className="text-slate-400">— {e.department}</span>}</span>
              </label>
            ))}
          </div>
        </div>
        <div className="flex justify-end gap-2 mt-4">
          <Btn variant="secondary" onClick={() => setNewOpen(false)}>Vazgeç</Btn>
          <Btn onClick={save} data-testid="trn-save"><GraduationCap size={12}/> Planla</Btn>
        </div>
      </Modal>

      {detail && (
        <Modal open={true} onClose={() => setDetail(null)} title={`Eğitim — ${detail.training_no}`} size="lg">
          <div className="grid grid-cols-2 gap-3 mb-3 text-sm">
            <div><b>Başlık:</b> {detail.title}</div>
            <div><b>Tarih:</b> {detail.training_date}</div>
            <div><b>Eğitmen:</b> {detail.trainer_name || detail.external_organization}</div>
            <div><b>Durum:</b> <Badge level={detail.status === "TAMAMLANDI" ? "ok" : "warn"}>{detail.status}</Badge></div>
          </div>
          <div className="text-xs font-semibold uppercase text-slate-600 mb-1">Katılımcılar</div>
          <table className="w-full text-xs border rounded">
            <thead className="bg-slate-50"><tr>
              <th className="text-left px-2 py-1">Personel</th><th className="text-left px-2 py-1">Departman</th>
              <th className="text-left px-2 py-1">Katılım</th><th className="text-left px-2 py-1">İşlem</th>
            </tr></thead>
            <tbody>{(detail.participants || []).map(p => (
              <tr key={p.id} className="border-t" data-testid={`trn-part-${p.id}`}>
                <td className="px-2 py-1">{p.employee_name_snapshot}</td>
                <td className="px-2 py-1">{p.department_snapshot || "—"}</td>
                <td className="px-2 py-1">
                  <Select value={p.attendance_status} onChange={e => updateAttendance(detail.id, p.employee_id, e.target.value)} className="w-32" data-testid={`trn-att-${p.employee_id}`}>
                    <option value="PLANLANDI">PLANLANDI</option>
                    <option value="KATILDI">KATILDI</option>
                    <option value="KATILMADI">KATILMADI</option>
                  </Select>
                </td>
                <td className="px-2 py-1 text-[10px] text-slate-500">{p.attendance_updated_at ? p.attendance_updated_at.slice(0, 16).replace("T", " ") : "—"}</td>
              </tr>
            ))}</tbody>
          </table>
          <div className="flex justify-end gap-2 mt-4">
            <Btn variant="ghost" onClick={() => dlPdf(`/trainings/${detail.id}/pdf`, `Egitim_${detail.training_no}.pdf`)} data-testid="trn-detail-pdf" title="Eğitim Kayıt PDF"><FileText size={12}/></Btn>
            <Btn variant="ghost" onClick={() => dlPdf(`/trainings/${detail.id}/attendance/pdf`, `Katilim_${detail.training_no}.pdf`)} data-testid="trn-detail-att-pdf"><Users size={12}/> Katılım PDF</Btn>
            {detail.status !== "TAMAMLANDI" && (
              <Btn onClick={() => complete(detail.id)} data-testid="trn-complete"><CheckCircle2 size={12}/> Tamamla</Btn>
            )}
            <Btn variant="danger" onClick={() => del(detail.id)} data-testid="trn-del"><Trash2 size={12}/></Btn>
          </div>
        </Modal>
      )}
    </div>
  );
}
